import React, {createContext, useContext, useState, useEffect} from "react";
import {useTranslation} from "react-i18next";
import {isAndroid} from "FirebaseUtils";
import swal from "sweetalert2";
import {LABEL_COLORS} from "constants/StyleConstants";
import {LOCAL_STORAGE_KEYS} from "constants/AppConstants.js";
import {registerPlugin} from "@capacitor/core";

const BackgroundGeolocation = registerPlugin<any>("BackgroundGeolocation");

const LocationContext = createContext<any>(undefined);

interface Props {
  children: React.ReactNode;
}

function LocationProvider({children}: Props) {
  const {t} = useTranslation();
  const [currentLocation, setCurrentLocation] = useState(null);
  const [isLocationLoading, setIsLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState(null);

  // Load cached location from localStorage
  const loadCachedLocation = () => {
    try {
      const cached = localStorage.getItem(LOCAL_STORAGE_KEYS.cachedLocation);
      if (cached) {
        const location = JSON.parse(cached);
        // Check if the cached location is not too old (e.g., less than 1 hour)
        const oneHourAgo = Date.now() - 60 * 60 * 1000;
        if (location.timestamp && location.timestamp > oneHourAgo) {
          console.log("Loading cached location:", location);
          return location;
        }
      }
    } catch (error) {
      console.error("Error loading cached location:", error);
    }
    return null;
  };

  // Save location to localStorage
  const saveLocationToCache = (location) => {
    try {
      const locationWithTimestamp = {
        ...location,
        timestamp: Date.now()
      };
      localStorage.setItem(LOCAL_STORAGE_KEYS.cachedLocation, JSON.stringify(locationWithTimestamp));
      console.log("Location saved to cache:", locationWithTimestamp);
    } catch (error) {
      console.error("Error saving location to cache:", error);
    }
  };

  const getCurrentLocation = () => {
    let lastLocation;
    let interval;
    return new Promise((resolve, reject) =>
      BackgroundGeolocation.addWatcher(
        {
          requestPermissions: false
        },
        async (location, error) => {
          if (error) {
            if (error.code === "NOT_AUTHORIZED") {
              const resultAlert = await swal.fire({
                title: t("location.servicesNeeded"),
                html: t("location.permissionNeeded"),
                icon: "warning",
                showDenyButton: true,
                confirmButtonText: t("common.yes"),
                denyButtonText: t("common.no"),
                showConfirmButton: true,
                confirmButtonColor: LABEL_COLORS.primary,
                reverseButtons: true
              });
              if (resultAlert.isConfirmed) {
                await BackgroundGeolocation.openSettings();
              }
            }
            console.error("Error while getting user location", error);
            reject(error);
          } else if (!location?.latitude || !location?.longitude) {
            console.error("No coordinates found");
            return;
          }
          lastLocation = location || undefined;
          resolve(lastLocation);
        }
      ).then((id) => {
        interval = setInterval(() => {
          if (lastLocation) {
            clearInterval(interval);
            BackgroundGeolocation.removeWatcher({id});
            resolve(lastLocation);
          }
        }, 200);
      })
    );
  };

  const initializeLocation = async () => {
    if (!isAndroid) return;

    try {
      setIsLocationLoading(true);
      setLocationError(null);

      // First, try to load cached location
      const cachedLocation = loadCachedLocation();
      if (cachedLocation) {
        setCurrentLocation(cachedLocation);
        console.log("Using cached location:", cachedLocation);
      }

      // Then get fresh location
      const location = await getCurrentLocation();
      setCurrentLocation(location);
      saveLocationToCache(location);
      console.log("Location initialized:", location);
    } catch (error) {
      console.error("Failed to initialize location:", error);
      setLocationError(error);
    } finally {
      setIsLocationLoading(false);
    }
  };

  const refreshLocation = async () => {
    await initializeLocation();
  };

  useEffect(() => {
    // Load cached location for all devices on startup
    const cachedLocation = loadCachedLocation();
    if (cachedLocation) {
      setCurrentLocation(cachedLocation);
      console.log("Loaded cached location on startup:", cachedLocation);
    }

    // Initialize fresh location at app startup for Android
    if (isAndroid) {
      initializeLocation();
    }
  }, []);

  const values = {
    currentLocation,
    isLocationLoading,
    locationError,
    refreshLocation,
    initializeLocation
  };

  return <LocationContext.Provider value={values}>{children}</LocationContext.Provider>;
}

function useLocation() {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error("useLocation must be used within a LocationProvider");
  }
  return context;
}

export {LocationProvider, useLocation};
